import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import {
  fetchAuditEvents, fetchQuarantine, restoreQuarantined, purgeQuarantined,
} from '../lib/queries.ts';
import type { AuditEvent, AuditFilters } from '../lib/types.ts';
import { AuditEventDetail } from './AuditEventDetail.tsx';

function formatBytes(n: number): string {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), u.length - 1);
  return `${(n / 1024 ** i).toFixed(1)} ${u[i]}`;
}

export function AuditPage(): React.ReactElement {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<'journal' | 'quarantine'>(
    searchParams.get('tab') === 'quarantine' ? 'quarantine' : 'journal',
  );

  // URL `?tab=` is the single source of truth: any change to it (including a
  // same-route navigation from the drawer's "view in quarantine" link) must
  // update the active tab. Buttons only write the param; they never set tab
  // directly, so there is no state/URL divergence or update loop.
  useEffect(() => {
    setTab(searchParams.get('tab') === 'quarantine' ? 'quarantine' : 'journal');
  }, [searchParams]);

  const selectTab = (key: 'journal' | 'quarantine'): void => {
    setSearchParams(key === 'quarantine' ? { tab: 'quarantine' } : {});
  };

  return (
    <>
      <div className="page-head">
        <h1>{t('audit.title')}</h1>
      </div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, borderBottom: '1px solid var(--line)' }}>
        {(['journal', 'quarantine'] as const).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => selectTab(key)}
            style={{
              padding: '8px 12px', border: 'none', cursor: 'pointer',
              background: tab === key ? 'var(--neon-soft)' : 'transparent',
              borderBottom: tab === key ? '2px solid var(--neon)' : '2px solid transparent',
            }}
          >
            {t(`audit.tabs.${key}`)}
          </button>
        ))}
      </div>
      {tab === 'journal' ? <JournalSection /> : <QuarantineSection />}
    </>
  );
}

const JOURNAL_ENTITY_TYPES = [
  'film', 'moment', 'clip', 'clip_variant', 'campaign', 'post_draft', 'post', 'account', 'user', 'auth',
] as const;

function JournalSection(): React.ReactElement {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<AuditFilters>({});
  const [selected, setSelected] = useState<AuditEvent | null>(null);
  const q = useQuery({ queryKey: ['audit', filters], queryFn: () => fetchAuditEvents(filters) });
  const events = q.data?.events ?? [];

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <select
          value={filters.action ?? ''}
          onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value || undefined }))}
        >
          <option value="">{t('audit.journal.all')}</option>
          {['created', 'updated', 'published', 'deleted', 'restored', 'purged', 'viewed', 'request'].map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        <select
          value={filters.entityType ?? ''}
          onChange={(e) => setFilters((f) => ({ ...f, entityType: e.target.value || undefined }))}
          aria-label={t('audit.journal.filterEntity')}
        >
          <option value="">{t('audit.journal.allEntities')}</option>
          {JOURNAL_ENTITY_TYPES.map((et) => (
            <option key={et} value={et}>{t(`audit.entityTypes.${et}`)}</option>
          ))}
        </select>
        <input
          type="search"
          value={filters.q ?? ''}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value || undefined }))}
          placeholder={t('audit.journal.searchPlaceholder')}
          aria-label={t('audit.journal.searchPlaceholder')}
        />
      </div>
      {events.length === 0 ? (
        <p>{t('audit.journal.empty')}</p>
      ) : (
        <div className="section">
          <div className="section-body no-pad">
            <table>
              <thead>
                <tr>
                  <th>{t('audit.journal.time')}</th>
                  <th>{t('audit.journal.actor')}</th>
                  <th>{t('audit.journal.action')}</th>
                  <th>{t('audit.journal.description')}</th>
                  <th>{t('audit.journal.status')}</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id} onClick={() => setSelected(e)} style={{ cursor: 'pointer' }}>
                    <td>{new Date(e.createdAt).toLocaleString()}</td>
                    <td>{e.actorEmail ?? '—'}</td>
                    <td>{e.action}</td>
                    <td>{(e.summary ?? `${e.method ?? ''} ${e.path ?? ''}`.trim()) || '—'}</td>
                    <td>{e.statusCode ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {selected && <AuditEventDetail event={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function QuarantineDetails({ details }: { details: Record<string, unknown> }): React.ReactElement | null {
  const { t } = useTranslation();
  const entries = Object.entries(details);
  if (entries.length === 0) return null;
  return (
    <tr>
      <td colSpan={8} style={{ background: 'var(--bg-soft)', padding: '10px 14px' }}>
        <h4 style={{ margin: '0 0 6px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-3)' }}>
          {t('audit.quarantine.details')}
        </h4>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {entries.map(([key, value]) => (
              <tr key={key}>
                <td style={{ padding: '4px 8px', fontSize: 12.5, color: 'var(--text-2)', width: '32%' }}>
                  {t(`audit.fields.${key}`, { defaultValue: key })}
                </td>
                <td style={{ padding: '4px 8px', fontSize: 12.5 }}>{value == null || value === '' ? '—' : String(value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </td>
    </tr>
  );
}

function QuarantineSection(): React.ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const q = useQuery({ queryKey: ['quarantine'], queryFn: () => fetchQuarantine() });
  const items = q.data?.items ?? [];
  const totalCount = items.length;
  const totalBytes = items.reduce((s, item) => s + item.sizeBytes, 0);
  const [previewErrors, setPreviewErrors] = useState<Set<string>>(new Set());
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['quarantine'] });
  };

  const markPreviewError = (key: string): void => {
    setPreviewErrors((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
  };

  const onRestore = async (type: string, id: string, label: string): Promise<void> => {
    if (!confirm(t('audit.quarantine.restoreConfirm', { label }))) return;
    try { await restoreQuarantined(type, id); invalidate(); }
    catch (e) { alert(e instanceof Error ? e.message : String(e)); }
  };

  const onPurge = async (type: string, id: string, label: string): Promise<void> => {
    if (!confirm(t('audit.quarantine.purgeConfirm', { label }))) return;
    try { await purgeQuarantined(type, id); invalidate(); }
    catch (e) { alert(e instanceof Error ? e.message : String(e)); }
  };

  return (
    <div>
      <p>{t('audit.quarantine.summary', { count: totalCount, size: formatBytes(totalBytes) })}</p>
      <p style={{ fontStyle: 'italic', fontSize: 12, color: 'var(--text-3)', marginTop: 6, marginBottom: 12 }}>
        {t('audit.quarantine.restoreNote')}
      </p>
      {items.length === 0 ? (
        <p>{t('audit.quarantine.empty')}</p>
      ) : (
        <div className="section">
          <div className="section-body no-pad">
            <table>
              <thead>
                <tr>
                  <th>{t('audit.quarantine.type')}</th>
                  <th>{t('audit.quarantine.label')}</th>
                  <th>{t('audit.quarantine.preview')}</th>
                  <th>{t('audit.quarantine.deletedAt')}</th>
                  <th>{t('audit.quarantine.deletedBy')}</th>
                  <th>{t('audit.quarantine.createdAt')}</th>
                  <th>{t('audit.quarantine.size')}</th>
                  <th>{t('audit.quarantine.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const key = `${item.type}:${item.id}`;
                  const hasPreviewError = previewErrors.has(key);
                  const hasDetails = Object.keys(item.details ?? {}).length > 0;
                  const isExpanded = expandedKey === key;
                  return (
                    <React.Fragment key={key}>
                      <tr
                        onClick={() => hasDetails && setExpandedKey(isExpanded ? null : key)}
                        style={{ cursor: hasDetails ? 'pointer' : 'default' }}
                      >
                        <td>
                          {/* Chevron marks rows that expand into a details panel. */}
                          {hasDetails && (
                            <span aria-hidden style={{ display: 'inline-block', width: 12, color: 'var(--text-3)' }}>
                              {isExpanded ? '▾' : '▸'}
                            </span>
                          )}
                          {item.type}
                        </td>
                        <td>{item.label}</td>
                        <td>
                          {item.previewUrl && !hasPreviewError ? (
                            <video
                              src={`${item.previewUrl}#t=0.1`}
                              controls
                              preload="metadata"
                              style={{ maxWidth: 220, maxHeight: 140 }}
                              onClick={(e) => e.stopPropagation()}
                              onError={() => markPreviewError(key)}
                            />
                          ) : (
                            <span style={{ color: 'var(--text-3)', fontSize: 12 }}>
                              {item.previewUrl && hasPreviewError
                                ? t('audit.quarantine.previewUnavailable')
                                : t('audit.quarantine.noPreview')}
                            </span>
                          )}
                        </td>
                        <td>{new Date(item.deletedAt).toLocaleString()}</td>
                        <td>{item.deletedByEmail ?? '—'}</td>
                        <td>{new Date(item.createdAt).toLocaleString()}</td>
                        <td>{formatBytes(item.sizeBytes)}</td>
                        <td>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); void onRestore(item.type, item.id, item.label); }}
                          >
                            {t('audit.quarantine.restore')}
                          </button>{' '}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); void onPurge(item.type, item.id, item.label); }}
                          >
                            {t('audit.quarantine.purge')}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && <QuarantineDetails details={item.details ?? {}} />}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
